"use client";
import {useSweetAlert} from "@/hooks/useSweetAlert";
import {addItemsToLocalStorage, getItemsFromLocalStorage} from '@/libs/local-storage.util';
import { createContext, useContext, useEffect, useState } from "react";
import {ProductActionStatus} from '@/libs/constants/common.constants';
import {useTranslations} from 'next-intl';

const WishlistContext = createContext(null);

// [{ product }]
export default function WishlistContextProvider({ children, products }) {
  const [wishlistStatus, setWishlistStatus] = useState(null);
  const [wishlistProducts, setWishlistProducts] = useState(Array.isArray(products) ? products : []);
  const toastAlert = useSweetAlert();
  const t = useTranslations('cart');
  
  useEffect(() => {
    if (Array.isArray(products)) {
      addItemsToLocalStorage('wishlist', products);
    }
    
    const wishlistProductFromLocalStorage = getItemsFromLocalStorage('wishlist');
    if (wishlistProductFromLocalStorage) {
      setWishlistProducts(wishlistProductFromLocalStorage);
    }
  }, [products]);
  
  const updateWishlist = (currentProduct) => {
    setWishlistProducts((prevWishlist) => {
      const updatedWishlist = [...prevWishlist];
      const index = updatedWishlist.findIndex(wish => wish.product.id === currentProduct.id);
      
      if (index !== -1) {
        updatedWishlist.splice(index, 1);
        setWishlistStatus(ProductActionStatus.deleted);
        toastAlert('success', t('deletedFromWishlist'));
      } else {
        updatedWishlist.push({
          product: {
            id: currentProduct.id,
            alt: currentProduct.alt,
            title: currentProduct.title,
            coverImage: currentProduct.coverImage,
            price: currentProduct.price
          }
        });
        setWishlistStatus(ProductActionStatus.added);
        toastAlert('success', t('addedToWishlist'));
      }
      
      addItemsToLocalStorage('wishlist', updatedWishlist);
      return updatedWishlist;
    });
  };
  
  const hasWishlist = (currentProduct) => {
    return wishlistProducts.some((wish) => wish.product.id === currentProduct.id);
  }
  
  const updateWishlistLocalStorage = (freshProducts) => {
    addItemsToLocalStorage('wishlist', freshProducts);
    setWishlistProducts(freshProducts);
  };
  
  return (
    <WishlistContext.Provider
      value={{
        wishlistProducts,
        setWishlistProducts,
        updateWishlist,
        wishlistStatus,
        hasWishlist,
        updateWishlistLocalStorage
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};
export const useWishlistContext = () => {
  return useContext(WishlistContext);
};
